/**
 * MONSTER TOWN — TEST HARNESS. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * `window.__harness` is how every screenshot, perf run and QA drive controls
 * the game. Crews ADD capabilities (a new named angle, a new demo layout, a new
 * phase) but must never change or remove an existing one — tools/*.mjs and the
 * critic loops are written against these names.
 *
 * Everything here must be synchronous or return a Promise that actually settles;
 * `drive.mjs` has a hard timeout and will kill a run that hangs.
 */

import { auditPlaceables, type AuditReport } from './core/placeable'
import { clock } from './core/time'
import { needs, routineNow } from './core/schedule'
import { errands, type ErrandStepKind } from './core/errands'
import { inventory } from './core/inventory'
import { availableNow, collection, getCollectible, type CollectionKind } from './core/collection'
import { engine, type PerfSample } from './core/engine'
import { runSaveSelfTest, save, type SaveTestReport } from './core/save'
import { world } from './core/scene'
import { wallet } from './core/economy'
import type { Rot } from './core/grid'
import type { FrameStats, GameMode, Phase, SaveSlot, SceneId, Vec3 } from './core/types'
import { app, type GameContext } from './app/context'
import { placeAll, selectPlaceable } from './build/placer'
import { teleportPlayer } from './monster/player'
import { allowFirstRunUnderAutomation } from './monster/creator'
import { demoLayout } from './content/blocks'
import { TOWN_SCENE_ID, townCenter } from './world/town'
import { kitStats } from './render/kit'
import { worldToCanvasPx } from './input/pick'
import { bus } from './core/events'

export interface NamedAngle {
  name: string
  sceneId: SceneId
  target: Vec3
  rot: Rot
  zoom: number
}

export interface HarnessApi {
  readonly ready: boolean
  readonly version: string
  // mode / phase
  setMode(mode: GameMode): void
  getMode(): GameMode
  setPhase(phase: Phase): void
  getPhase(): Phase
  // camera + navigation
  angles(): string[]
  angle(name: string): boolean
  spots(): string[]
  teleport(spot: string): boolean
  setRotation(rot: Rot): void
  setZoom(index: number): void
  // scenes
  scenes(): string[]
  /** Which scene is awake right now. Additive; nothing existing changed. */
  activeScene(): string | null
  enterScene(id: string): Promise<boolean>
  enterHouse(): Promise<boolean>
  enterVenue(id?: string): Promise<boolean>
  // content
  placeDemo(scene: string): Promise<number>
  select(defId: string): void
  /** Empties every placement grid AND strips the crews' baked scenery. */
  clearTown(): void
  /** Puts the baked scenery `clearTown()` stripped back. Additive. */
  restoreTown(): void
  // audits
  auditPlaceables(): AuditReport
  saveSelfTest(): { ok: boolean; results: SaveTestReport[] }
  // perf + determinism
  seed(n: number): void
  stats(): FrameStats & { placeables: number; scenes: number; materials: number; geometries: number }
  perf(longFrameMs?: number): PerfSample
  resetPerf(): void
  setRenderScale(v: number): void
  setPixelRatioCap(v: number): void
  setAdaptive(on: boolean): void
  setQuality(tier: 'high' | 'medium' | 'low'): void
  // saves
  slot(): SaveSlot
  /** Commits the current slot, switches, and hydrates the other one. */
  setSlot(slot: SaveSlot): void
  saveNow(): boolean
  eraseSlot(slot: SaveSlot): void
  /**
   * Wipe a slot and replay the REAL first launch: the monster creator opens,
   * exactly as it does for a kid opening the app for the first time.
   *
   * The creator deliberately does not open under an automated browser, so that
   * `shot`/`perf`/`drive` are not run through a takeover screen they never
   * asked for — which also meant NO tool could ever look at the first thing a
   * kid sees, and a whole critic round reported "turn one shows nothing of the
   * kid's" from a capture that had skipped turn one. This is the opt-in.
   */
  firstRun(slot: SaveSlot): Promise<void>
  // sync
  settle(frames?: number): Promise<void>

  // -- INTEGRATION PLAYTEST (additive) ------------------------------------
  // These exist so a QA run can play the game the way a kid does: through the
  // real touch router and the real canvas bounding rect, never by calling a
  // crew's internals. `tapAt`/`tapInstance` are the only sanctioned way for a
  // tool to produce a tap at a world position.
  /** Canvas CSS px of a world point, via the same projection the HUD uses. */
  screenPx(world: Vec3): { x: number; y: number; behind: boolean }
  /** Real tap at a world point. Returns false if it is off-canvas. */
  tapAt(world: Vec3): boolean
  /** Real tap on a placed instance's centre. Returns false if it is not placed. */
  tapInstance(instanceId: string): boolean
  /** Every placement in a scene (default: the active one). */
  instances(sceneId?: string): Array<{
    instanceId: string
    defId: string
    anchor: { gx: number; gy: number; gz: number }
    centre: Vec3
  }>
  /** Where the player monster is standing right now. */
  playerPos(): Vec3
  /** Record every bus event of the given names until the returned drain is called. */
  watch(names: readonly string[]): void
  /** Events captured since `watch()`, then clears the log. */
  drain(): Array<{ name: string; payload: unknown }>

  // -- "A DAY IN TOWN" (additive) -----------------------------------------
  //
  // Everything a downstream crew or a critic needs to put the world into a
  // named state WITHOUT waiting sixteen real minutes for nightfall, and without
  // reaching into a crew's internals. Nothing here exists in the shipped game's
  // UI — these are the same calls the game makes, aimed by a tool.

  /** The whole clock in one read. */
  timeNow(): {
    day: number
    t: number
    hour: number
    phase: string
    phaseT: number
    darkness: number
    paused: boolean
  }
  /**
   * Put the clock at an hour (0..24) or at the START of a named band
   * ('dawn'|'day'|'dusk'|'night'). Returns the resulting hour.
   */
  setTimeOfDay(hourOrPhase: number | string): number
  /** Move the clock forward by real ms. Deterministic; the same call in a test. */
  advanceTime(ms: number): number
  /** THE BED, without having to find the bed. Lands on the next morning. */
  sleep(): { day: number; hour: number }
  /** Roll a whole day forward, landing on the same hour. */
  advanceDay(): number
  /** Real ms per in-world day. Shrink it to watch a full cycle in seconds. */
  setDayLength(ms: number): void
  /** Freeze/unfreeze the world clock under the reason 'harness'. */
  pauseTime(on: boolean): void
  /** What a routine table says `id` is doing right now. */
  routineOf(id: string): { activity: string; where: string | null; t: number } | null
  /** Every gentle need, and how warmly it is inviting. Never a meter. */
  needsNow(): Array<{ id: string; warmth: number; inviting: boolean }>

  // errands
  /** Hand the kid an errand. Returns false only for an unknown id. */
  grantErrand(id: string): boolean
  /** Every running errand's card, flattened. */
  errandState(): Array<{
    id: string
    step: number
    steps: number
    kind: string | null
    have: number
    need: number
    hidden: boolean
  }>
  /** Report that something happened, exactly as a crew would. Steps advanced. */
  observe(kind: string, what: string, count?: number, extra?: Record<string, string>): number
  /** Jump an errand to finished (QA only — no kid-reachable path does this). */
  completeErrand(id: string): boolean
  /** Errand ids finished in this town. */
  errandsDone(): string[]

  // inventory + collection
  giveItem(itemId: string, count?: number): number
  inventoryState(): Record<string, number>
  /** Mark a creature/recipe found WITHOUT the discovery moment. */
  setFound(id: string, count?: number): void
  /** Fire the real discovery, exactly as catching it would. */
  findCollectible(id: string): boolean
  collectionState(): Array<{ kind: string; found: number; total: number }>
  /** What could be found right now at a place tag. Proves the time rules. */
  availableNow(kind: string, place?: string): string[]

  // visiting the other kid's town
  /** Open the OTHER slot read-only. False if there is no town there yet. */
  beginVisit(slot: SaveSlot): boolean
  endVisit(): boolean
  isVisiting(): { visiting: boolean; slot: SaveSlot | null; host: SaveSlot | null }

  // -- honesty helpers for the tools --------------------------------------
  /**
   * True while a scene transition is in flight. A door is a placeable, so a tap
   * can start one; a tool that taps and then asks `activeScene()` on the very
   * next frame gets the room it is LEAVING and then silently ends up somewhere
   * else. See the LAW #1 sweep in `tools/playthrough.mjs`.
   */
  transitioning(): boolean
  /** Is this world point currently on the canvas at all? */
  onScreen(world: Vec3): boolean
}

declare global {
  interface Window {
    __harness?: HarnessApi
  }
}

const ANGLES = new Map<string, NamedAngle>()

/** Crews register their own angles here as scenes come online. */
export function registerAngle(a: NamedAngle): void {
  ANGLES.set(a.name, a)
}

const SPOTS = new Map<string, { sceneId: SceneId; pos: Vec3 }>()

export function registerSpot(name: string, sceneId: SceneId, pos: Vec3): void {
  SPOTS.set(name, { sceneId, pos })
}

/** Demo layout providers, keyed by the name `placeDemo()` takes. */
const DEMOS = new Map<string, () => Promise<number>>()

export function registerDemo(name: string, fn: () => Promise<number>): void {
  DEMOS.set(name, fn)
}

/**
 * SCENERY RESETTERS — how `clearTown()` reaches a crew's BAKED world.
 *
 * `clearTown()` only ever emptied the placement grids. Everything a crew builds
 * into `statics` and `ground` at install time — the house, the street, the
 * crossings, the lamp posts, the neighbouring roofs — survived it untouched, so
 * a tool that called `clearTown()` and took a picture got a picture of the full
 * town and the empty-plot state could not be reviewed at all. The first thing a
 * kid ever sees was the one state the critic loop could not look at.
 *
 * A crew registers how to strip and restore its own scenery; the harness never
 * reaches into another crew's scene graph.
 */
export interface SceneryReset {
  /** Hide the baked scenery and drop whatever it blocks. */
  clear(): void
  /** Put it all back. */
  restore(): void
}

const RESETS: SceneryReset[] = []

export function registerSceneryReset(r: SceneryReset): void {
  RESETS.push(r)
}

const watchOffs: Array<() => void> = []
const watchLog: Array<{ name: string; payload: unknown }> = []

/** First registered venue room, derived — never a hard-coded scene id. */
function defaultVenueScene(): string {
  return world.ids().find((id) => id.startsWith('venue_')) ?? 'venue_iceCream'
}

export function installHarness(ctx: GameContext): void {
  const centre = townCenter()

  // Named angles. `house-exterior`, `interior`, `venue` and `monster-creator`
  // resolve to the town framing until those scenes exist — the tools already
  // reference them, and a missing angle must never crash a shot run.
  registerAngle({ name: 'town', sceneId: TOWN_SCENE_ID, target: centre, rot: 0, zoom: 3 })
  registerAngle({ name: 'town-close', sceneId: TOWN_SCENE_ID, target: centre, rot: 0, zoom: 1 })
  registerAngle({ name: 'house-exterior', sceneId: TOWN_SCENE_ID, target: centre, rot: 1, zoom: 2 })
  registerAngle({ name: 'interior', sceneId: TOWN_SCENE_ID, target: centre, rot: 0, zoom: 1 })
  registerAngle({ name: 'venue', sceneId: TOWN_SCENE_ID, target: centre, rot: 2, zoom: 1 })
  registerAngle({ name: 'monster-creator', sceneId: TOWN_SCENE_ID, target: centre, rot: 0, zoom: 0 })

  registerSpot('center', TOWN_SCENE_ID, centre)
  registerSpot('corner', TOWN_SCENE_ID, { x: centre.x - 14, y: 0, z: centre.z - 14 })

  registerDemo('town', async () => {
    const items = demoLayout()
    placeAll(TOWN_SCENE_ID, items)
    await settle(2)
    return items.length
  })

  const api: HarnessApi = {
    ready: true,
    version: '0.1.0',

    setMode: (mode) => app.setMode(mode),
    getMode: () => app.mode,
    setPhase: (phase) => app.setPhase(phase),
    getPhase: () => app.phase,

    angles: () => [...ANGLES.keys()],
    angle: (name) => {
      const a = ANGLES.get(name)
      if (a === undefined) return false
      if (world.activeSceneId !== a.sceneId && world.has(a.sceneId)) {
        world.setActiveImmediate(a.sceneId)
      }
      ctx.camera.setRotation(a.rot, true)
      ctx.camera.setZoomIndex(a.zoom, true)
      // HOLD it. The player system re-aims at the monster every render frame, so
      // a plain lookAtPoint here was overwritten before the screenshot and every
      // angle that frames something other than the player quietly produced a
      // picture of the player instead.
      ctx.camera.holdAt(a.target)
      return true
    },

    spots: () => [...SPOTS.keys()],
    teleport: (spot) => {
      const s = SPOTS.get(spot)
      if (s === undefined) return false
      if (world.has(s.sceneId) && world.activeSceneId !== s.sceneId) {
        world.setActiveImmediate(s.sceneId)
      }
      // Teleporting moves the MONSTER, so following it again is the point.
      ctx.camera.releaseHold()
      teleportPlayer(s.pos)
      ctx.camera.lookAtPoint(s.pos, true)
      return true
    },

    setRotation: (rot) => ctx.camera.setRotation(rot, true),
    setZoom: (i) => ctx.camera.setZoomIndex(i, true),

    scenes: () => world.ids(),
    /**
     * ADDED SO A TOOL CAN AVOID A TRANSITION IT DOES NOT NEED.
     *
     * `playthrough`'s LAW #1 sweep re-entered the room after EVERY prop, because
     * tapping a door legitimately takes you out and would silently end the
     * sweep. A door is one prop in ten; the other nine paid a full scene
     * transition plus a settle for nothing, and the run was blowing its own
     * watchdog three rooms in. Now it can ask.
     */
    activeScene: () => (world.activeSceneId === null ? null : String(world.activeSceneId)),
    enterScene: async (id) => {
      if (!world.has(id as SceneId)) return false
      // A CHANGE OF SCENE ALWAYS RELEASES THE CAMERA HOLD. See `angle()` below:
      // the hold pins the framing against the player follow, and nothing but
      // `teleport()` used to let go of it. A tool that framed one scene and then
      // walked into another got a camera still staring at the room it left, and
      // three critic captures came back as empty backdrop with only the HUD on
      // them. The crews frame their own rooms on `scene:entered` via
      // `lookAtPoint`, which a held camera ignores — so releasing here is what
      // lets that framing land.
      ctx.camera.releaseHold()
      await world.transitionTo(id as SceneId, { instant: true })
      await settle(2)
      return true
    },
    enterHouse: async () => {
      const id = 'house' as SceneId
      if (!world.has(id)) return false
      ctx.camera.releaseHold()
      await world.transitionTo(id, { instant: true })
      await settle(2)
      return true
    },
    // NO DEAD DEFAULT ID. This defaulted to `'venue1'`, a scene that has never
    // been registered, which is the same dead id that made the ice cream shop's
    // front door do nothing. The default is now DERIVED from what is actually
    // registered, so it cannot rot.
    enterVenue: async (id) => {
      const sid = (id ?? defaultVenueScene()) as SceneId
      if (!world.has(sid)) return false
      ctx.camera.releaseHold()
      await world.transitionTo(sid, { instant: true })
      await settle(2)
      return true
    },

    placeDemo: async (scene) => {
      const fn = DEMOS.get(scene)
      if (fn === undefined) return 0
      return fn()
    },
    select: (defId) => selectPlaceable(defId),
    clearTown: () => {
      for (const id of world.ids()) {
        const node = world.require(id)
        node.grid.clear()
        for (const [, obj] of node.art) node.placeables.remove(obj)
        node.art.clear()
      }
      // ...and the BAKED world too, which is what "clear the town" has always
      // meant to everything that calls this. `restoreTown()` puts it back.
      for (const r of RESETS) r.clear()
    },
    restoreTown: () => {
      for (const r of RESETS) r.restore()
    },

    auditPlaceables: () => {
      const id = world.activeSceneId
      const grid = id === null ? world.require(TOWN_SCENE_ID).grid : world.require(id).grid
      return auditPlaceables(grid)
    },
    saveSelfTest: () => runSaveSelfTest(),

    seed: (n) => {
      engine.seed(n)
      wallet.rng = engine.rng
    },
    stats: () => {
      const k = kitStats()
      let placeables = 0
      for (const id of world.ids()) placeables += world.require(id).grid.count()
      return {
        ...engine.stats,
        placeables,
        scenes: world.ids().length,
        materials: k.materials,
        geometries: k.geometries,
      }
    },
    perf: (longFrameMs = 50) => engine.perf(longFrameMs),
    resetPerf: () => engine.resetPerf(),
    setRenderScale: (v) => ctx.renderer.setRenderScale(v),
    setPixelRatioCap: (v) => ctx.renderer.setMaxPixelRatio(v),
    setAdaptive: (on) => ctx.renderer.setAdaptive(on),
    setQuality: (tier) => ctx.renderer.setTier(tier),

    slot: () => save.activeSlot,
    setSlot: (slot) => {
      save.useSlot(slot)
    },
    saveNow: () => save.flush('harness'),
    eraseSlot: (slot) => save.eraseSlot(slot),
    firstRun: async (slot) => {
      allowFirstRunUnderAutomation()
      ctx.camera.releaseHold()
      // HARD erase: `eraseSlot` keeps the backup, so a second `firstRun()` in
      // the same session recovered the monster created by the first one and the
      // creator never opened again. That made the end-to-end journey step look
      // like it had skipped turn one.
      save.eraseSlotHard(slot)
      save.useSlot(slot)
      // `maybeOpenOnFirstLaunch()` is deferred one tick off `save:loaded`.
      await settle(10)
    },

    settle: (frames = 3) => settle(frames),

    screenPx: (w) => worldToCanvasPx(ctx.camera.camera, ctx.canvas, w),
    tapAt: (w) => {
      const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, w)
      const r = ctx.canvas.getBoundingClientRect()
      if (p.behind || p.x < 0 || p.y < 0 || p.x > r.width || p.y > r.height) return false
      ctx.touch.simulateTapAtCanvasPx(p.x, p.y)
      return true
    },
    tapInstance: (instanceId) => {
      const id = world.activeSceneId
      if (id === null) return false
      const node = world.require(id)
      const pl = node.grid.placement(instanceId as never)
      if (pl === null) return false
      const c = node.grid.footprintCenter(pl.anchor, pl.footprint, pl.rot)
      return api.tapAt({ x: c.x, y: c.y + 0.4, z: c.z })
    },
    instances: (sceneId) => {
      const id = (sceneId ?? world.activeSceneId) as SceneId | null
      if (id === null || !world.has(id)) return []
      const node = world.require(id)
      return node.grid.all().map((p) => {
        const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
        return {
          instanceId: p.instanceId as string,
          defId: p.defId as string,
          anchor: { gx: p.anchor.gx, gy: p.anchor.gy, gz: p.anchor.gz },
          centre: { x: c.x, y: c.y, z: c.z },
        }
      })
    },
    playerPos: () => ({ ...app.playerPos }),
    watch: (names) => {
      for (const off of watchOffs) off()
      watchOffs.length = 0
      watchLog.length = 0
      for (const n of names) {
        watchOffs.push(
          bus.on(n as never, ((payload: unknown) => {
            if (watchLog.length < 500) watchLog.push({ name: n, payload })
          }) as never),
        )
      }
    },
    drain: () => {
      const out = [...watchLog]
      watchLog.length = 0
      return out
    },

    // -- "A DAY IN TOWN" ---------------------------------------------------

    timeNow: () => {
      const s = clock.now()
      return {
        day: s.day,
        t: s.t,
        hour: s.hour,
        phase: s.phase,
        phaseT: s.phaseT,
        darkness: s.darkness,
        paused: s.paused,
      }
    },
    setTimeOfDay: (hourOrPhase) => {
      if (typeof hourOrPhase === 'number') return clock.setHour(hourOrPhase).hour
      const band = clock.dayPhases().find((b) => b.id === hourOrPhase)
      // A band's start plus a nudge, so `phase` reads as the band asked for and
      // never lands exactly on the boundary of the previous one.
      const t = band === undefined ? 7 / 24 : band.from + 0.01
      return clock.setHour(t * 24).hour
    },
    advanceTime: (ms) => clock.advance(ms).hour,
    sleep: () => {
      const s = clock.sleepToMorning()
      return { day: s.day, hour: s.hour }
    },
    advanceDay: () => clock.advance(clock.dayLengthMs).day,
    setDayLength: (ms) => clock.setDayLengthMs(ms),
    pauseTime: (on) => (on ? clock.pause('harness') : clock.resume('harness')),
    routineOf: (id) => {
      const r = routineNow(id)
      return r === null ? null : { activity: r.activity, where: r.where, t: r.t }
    },
    needsNow: () => needs.now().map((n) => ({ id: n.id, warmth: n.warmth, inviting: n.inviting })),

    grantErrand: (id) => errands.grant(id) !== null,
    errandState: () =>
      errands.cards().map((c) => ({
        id: c.id,
        step: c.stepIndex,
        steps: c.def.steps.length,
        kind: c.step?.kind ?? null,
        have: c.have,
        need: c.need,
        hidden: c.hidden,
      })),
    observe: (kind, what, count = 1, extra = {}) =>
      errands.observe({
        kind: kind as ErrandStepKind,
        what,
        count,
        ...(extra['to'] === undefined ? {} : { to: extra['to'] }),
        ...(extra['sceneId'] === undefined ? {} : { sceneId: extra['sceneId'] }),
        ...(extra['group'] === undefined ? {} : { group: extra['group'] }),
        ...(extra['category'] === undefined ? {} : { category: extra['category'] }),
      }),
    completeErrand: (id) => errands.forceComplete(id),
    errandsDone: () => errands.completedIds(),

    giveItem: (itemId, count = 1) => inventory.add(itemId, count, 'harness'),
    inventoryState: () => inventory.all(),
    setFound: (id, count = 1) => collection.setFound(id, count),
    findCollectible: (id) => {
      if (getCollectible(id) === undefined) return false
      collection.found(id)
      return true
    },
    collectionState: () =>
      (['fish', 'bug', 'recipe', 'animal'] as const).map((kind) => ({
        kind,
        ...collection.completion(kind),
      })),
    availableNow: (kind, place) =>
      availableNow(kind as CollectionKind, place).map((e) => e.id),

    beginVisit: (slot) => save.beginVisit(slot),
    endVisit: () => save.endVisit() !== null,
    isVisiting: () => ({
      visiting: save.visiting,
      slot: save.visitSlot,
      host: save.visitHostSlot,
    }),

    transitioning: () => world.transitioning,
    onScreen: (w) => {
      const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, w)
      const r = ctx.canvas.getBoundingClientRect()
      return !p.behind && p.x >= 0 && p.y >= 0 && p.x <= r.width && p.y <= r.height
    },
  }

  // A REAL transition lets go of a harness hold as well, so a pinned framing can
  // never survive a scene change — for a tool or for a kid walking into a shop.
  bus.on('scene:transitionStart', () => ctx.camera.releaseHold())

  window.__harness = api
}

/** Wait for N animation frames so a screenshot never catches a half-built scene. */
export function settle(frames = 3): Promise<void> {
  return new Promise<void>((resolve) => {
    let n = Math.max(1, frames)
    const step = (): void => {
      n--
      if (n <= 0) resolve()
      else requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  })
}
